import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog as TwDialog,
  Slider,
  Input,
} from "@material-tailwind/react";
import { observer } from "mobx-react-lite";

import { StockistIssue } from "../../../Common/DTOs/StockistIssue";
import { storageController } from "../../../Common/Controllers/StoreControllerFactory";

type DialogProps = {
  issue: StockistIssue
  orderDialogOpen: boolean
  setOrderDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const Dialog = observer(({
                                  issue,
                                  orderDialogOpen,
                                  setOrderDialogOpen,
                                }: DialogProps) => {
  const store = storageController.getOrCreateStore(issue);
  const [selectedTierQuantity, setSelectedTierQuantity] = useState<number | null>(null);

  useEffect(() => {
    if (orderDialogOpen) {
      void store.fetchPromotionSummary(issue.id);
      void store.fetchFreebieEligibility(issue.magazine.publisherId);
    }
  }, [issue.id, orderDialogOpen]);

  const sortedDiscounts = useMemo(() => {
    if (!issue.mostSpecificDiscounts || issue.mostSpecificDiscounts.length === 0) return [];
    return [...issue.mostSpecificDiscounts].sort((a, b) => a.quantityFrom - b.quantityFrom);
  }, [issue.mostSpecificDiscounts]);

  const activeDiscount = useMemo(() => {
    const currentOrderQty = store.orderedAmount ?? 0;
    const totalPurchasedQty = store.promotionSummary?.totalPurchased ?? 0;
    const totalQty = currentOrderQty + totalPurchasedQty;

    const applicable = sortedDiscounts.filter(d => totalQty >= d.quantityFrom);
    if (applicable.length === 0) return null;
    return applicable.reduce((prev, cur) => (cur.quantityFrom > prev.quantityFrom ? cur : prev), applicable[0]);
  }, [sortedDiscounts, store.orderedAmount, store.promotionSummary?.totalPurchased]);

  useEffect(() => {
    if (activeDiscount) {
      setSelectedTierQuantity(activeDiscount.quantityFrom);
    } else {
      setSelectedTierQuantity(null);
    }
  }, [activeDiscount?.quantityFrom]);

  const handleTierClick = (tierQty: number) => {
    const totalPurchasedQty = store.promotionSummary?.totalPurchased ?? 0;
    const amountToOrder = Math.max(1, tierQty - totalPurchasedQty);
    if (store.orderedAmount === amountToOrder) {
      setSelectedTierQuantity(tierQty);
      return;
    }
    store.onCopiesChange(amountToOrder);
    setSelectedTierQuantity(tierQty);
  };

  const maxAvailable = issue.stock.issuesAvailableInTotal ?? 0;
  const issuePrice = issue.price;

  const bestDiscountPercentage = useMemo(() => {
    const currentOrderQty = store.orderedAmount ?? 0;
    const totalPurchasedQty = store.promotionSummary?.totalPurchased ?? 0;
    const totalQty = currentOrderQty + totalPurchasedQty;

    const applicable = sortedDiscounts.filter(d => totalQty >= d.quantityFrom);
    if (!applicable || applicable.length === 0) return 0;
    return Math.max(...applicable.map(d => d.discountPercentage));
  }, [sortedDiscounts, store.orderedAmount, store.promotionSummary?.totalPurchased]);

  const discountedPricePerIssue = useMemo(() => {
    return +(issuePrice * (1 - (bestDiscountPercentage / 100))).toFixed(2);
  }, [issuePrice, bestDiscountPercentage]);

  const nextDiscountInfo = useMemo(() => {
    const currentOrderQty = store.orderedAmount ?? 0;
    const totalPurchasedQty = store.promotionSummary?.totalPurchased ?? 0;
    const totalQty = currentOrderQty + totalPurchasedQty;

    const next = sortedDiscounts.find(d => totalQty < d.quantityFrom);
    if (!next) return null;
    return {remaining: next.quantityFrom - totalQty, next};
  }, [sortedDiscounts, store.orderedAmount, store.promotionSummary?.totalPurchased]);

  const FREEBIE_EVERY_N = 10;

  const freebieInfo = useMemo(() => {
    const freebieEligibleQty = store.promotionSummary?.freebieEligibleQuantity ?? 0;
    const freebiesEarned = store.promotionSummary?.freebiesEarned ?? 0;
    const freebiesGranted = store.promotionSummary?.freebiesGranted ?? 0;

    const newEligibleTotal = freebieEligibleQty + (store.orderedAmount ?? 0);
    const rawProgress = newEligibleTotal % FREEBIE_EVERY_N;
    const progressToNextFreebie = rawProgress === 0 && newEligibleTotal > 0 ? FREEBIE_EVERY_N : rawProgress;
    const copiesToNextFreebie = FREEBIE_EVERY_N - (rawProgress === 0 ? 0 : rawProgress);
    const freebiesPending = Math.max(0, freebiesEarned - freebiesGranted);

    return {progressToNextFreebie, copiesToNextFreebie, freebiesPending};
  }, [store.orderedAmount, store.promotionSummary]);

  const onConfirmClickHandler = () => {
    setOrderDialogOpen(false);
    void store.confirmOrder(issue);
  };

  return (
    <TwDialog
      animate={{mount: {translateY: 0}, unmount: {translateY: 0}}}
      open={orderDialogOpen}
      handler={() => setOrderDialogOpen(!orderDialogOpen)}
      className="
flex flex-col bg-main/90 text-light-grey border border-light-grey rounded-none md:!rounded-xl shadow-2xl 
p-2 md:p-6 max-h-[900px] !m-0 !w-full !h-[100dvh] max-w-full md:!max-w-[680px] lg:!max-w-[800px] xl:!max-w-[1000px] xl:!max-w-[1280px] 
!overflow-y-hidden"
    >
      <div
        className="flex-none border-b border-light-grey/40 flex flex-col items-center justify-center text-center pt-4 pb-6">
        <span className="text-lg md:text-3xl font-bold">{issue.magazine.title}</span>
        <span className="text-xs md:text-lg italic text-light-grey">{issue.identity}</span>
      </div>

      <div className="flex-auto overflow-y-auto p-4 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center h-full">
          <div className="flex flex-col gap-4 md:gap-12">
            <div className="grid grid-cols-12 gap-3 items-center">
              <label className="col-span-4 text-sm md:text-lg text-light-grey">Copies</label>
              <div className="col-span-5">
                <Input
                  className="bg-main text-white md:text-lg"
                  containerProps={{className: "min-w-[90px] w-full"}}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value || "0", 10) || 0;
                    store.onCopiesChange(Math.max(0, Math.min(parsed, maxAvailable)));
                  }}
                  label={`Up to ${maxAvailable}`}
                  value={String(store.orderedAmount ?? "")}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              {store.isFreebieEligible && (
                <div className="col-span-3 flex flex-col items-center leading-tight">
                  <span
                    className={`text-sm font-bold ${store.freebiesFromCurrentOrder > 0 ? "text-amber-300" : "text-light-grey/40"}`}>
                    +{store.freebiesFromCurrentOrder} free
                  </span>
                  {store.freebiesFromCurrentOrder === 0 && freebieInfo && (
                    <span className="text-xs text-light-grey/40 text-center">
                      {freebieInfo.copiesToNextFreebie} more
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-sm text-light-grey mb-2 md:text-lg">Adjust amount</div>
              <div className="px-2">
                <Slider
                  onChange={(value) => store.onSliderChange(value)}
                  value={store.sliderPercentage}
                  className="w-full md:text-lg"
                />
                <div className="flex justify-between text-xs text-light-grey mt-2 md:text-lg">
                  <span>0</span>
                  <span>{maxAvailable}</span>
                </div>
                {store.isFreebieEligible && freebieInfo && (
                  <div className="mt-3">
                    <div className="w-full bg-dark-grey/60 rounded-full h-1.5">
                      <div
                        className="bg-amber-400 h-1.5 rounded-full transition-all duration-300"
                        style={{width: `${(freebieInfo.progressToNextFreebie / FREEBIE_EVERY_N) * 100}%`}}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-light-grey/50 mt-1">
                      <span>🎁 {freebieInfo.progressToNextFreebie}/{FREEBIE_EVERY_N} toward next free copy</span>
                      {freebieInfo.freebiesPending > 0 && (
                        <span className="text-amber-300/70">{freebieInfo.freebiesPending} pending</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 items-center">
              <label className="col-span-4 text-sm md:text-lg text-light-grey">Price</label>
              <div
                className="col-span-8 font-semibold text-2xl md:text-3xl text-green-500">£{String(store.orderedPrice ?? "")}</div>
            </div>

            <div className="border-t border-light-grey/40 pt-4 flex flex-col gap-2">
              <div className="grid grid-cols-12 gap-3 items-center">
                <label className="col-span-4 text-sm text-light-grey">Weight</label>
                <div className="col-span-8 text-sm">{store.orderedWeightKilograms?.toFixed(2)}kg</div>
              </div>
              {(store.promotionSummary?.totalPurchased ?? 0) > 0 && (
                <div className="grid grid-cols-12 gap-3 items-center">
                  <label className="col-span-4 text-sm text-light-grey">Previously ordered</label>
                  <div className="col-span-8 text-sm text-amber-300 font-medium">
                    {store.promotionSummary!.totalPurchased} cop{store.promotionSummary!.totalPurchased !== 1 ? "ies" : "y"}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 items-center md:items-stretch justify-start py-4">
            <div className="flex flex-col gap-4 w-full items-center">
              <img
                src={issue.imageURL_230x320}
                alt={`${issue.magazine.title} cover`}
                className="w-[230px] h-[320px] object-cover rounded-md shadow-md"
              />

              <div className="flex items-baseline gap-3">
                <div
                  className={`text-2xl font-bold transition-all duration-300 ${bestDiscountPercentage > 0
                    ? "line-through text-light-grey/70"
                    : "text-white"
                  }`}
                >
                  £{issuePrice.toFixed(2)}
                </div>

                {bestDiscountPercentage > 0 && (
                  <div className="relative">
                    <div className="text-2xl font-semibold text-white transition-opacity duration-300">
                      £{discountedPricePerIssue.toFixed(2)}
                    </div>
                    <div
                      className="text-xs text-light-grey bg-green-600 p-1 rounded absolute top-0 right-0 -translate-y-1/2 translate-x-[100%]">
                      -{bestDiscountPercentage}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {sortedDiscounts.length > 0 && (
              <div className="w-full">
                <div className="text-sm text-light-grey mb-2">Quantity discounts</div>
                <div className="flex gap-2 w-full">
                  {sortedDiscounts.map((discount, idx) => {
                    const orderedAmount = store.orderedAmount ?? 0;
                    const totalPurchasedQty = store.promotionSummary?.totalPurchased ?? 0;
                    const isAchieved = (orderedAmount + totalPurchasedQty) >= discount.quantityFrom;
                    const isSelected = selectedTierQuantity === discount.quantityFrom;
                    const minCopiesNeeded = discount.quantityFrom - totalPurchasedQty;
                    const isDisabled = minCopiesNeeded <= 0 || minCopiesNeeded > store.maxOrderable;

                    const tierColors = [
                      "bg-green-300",
                      "bg-green-400",
                      "bg-green-500",
                      "bg-green-600",
                      "bg-green-700",
                      "bg-green-800",
                    ];
                    const tierColor = tierColors[Math.min(idx, tierColors.length - 1)];

                    return (
                      <button
                        key={idx}
                        onClick={() => handleTierClick(discount.quantityFrom)}
                        disabled={isDisabled}
                        className={`
                          flex-1 flex gap-2 justify-between items-center px-2 py-2 rounded-md border transition-all duration-300
                          disabled:opacity-50 disabled:hover:scale-100
                          ${isSelected
                          ? "border-green-400 bg-green-700/40"
                          : isAchieved
                            ? "border-light-grey/40 bg-dark-grey/40"
                            : `border-light-grey/40 ${tierColor}/30`}
                          hover:scale-[1.03]
                        `}
                      >
                        <div className="text-sm font-medium text-light-grey">
                          {discount.quantityFrom}
                        </div>
                        <div className="text-lg font-bold text-white">
                          -{discount.discountPercentage}%
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4 text-lg text-white text-center">
              {nextDiscountInfo ? (
                <>
                  Order{" "}
                  <span className="font-bold">{nextDiscountInfo.remaining}</span>{" "}
                  more for{" "}
                  <span className="font-bold bg-green-600 px-2 py-1 rounded">
                    -{nextDiscountInfo.next.discountPercentage}%
                  </span>
                </>
              ) : bestDiscountPercentage > 0 ? (
                <>You've unlocked the best available discount!</>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="flex-none pt-2 md:pt-4 overflow-y-hidden border-t border-light-grey/40 flex justify-center md:justify-end items-center gap-3">
        <Button
          variant="text"
          color="red"
          size="lg"
          onClick={() => setOrderDialogOpen(false)}
          className="!text-base p-3 md:py-4 md:px-6"
        >
          Cancel
        </Button>
        <Button
          disabled={store.priceInputError}
          size="lg"
          onClick={onConfirmClickHandler}
          className="!text-base p-3 md:py-4 md:px-6 bg-purple hover:bg-purple/80"
        >
          Add to basket
        </Button>
      </div>
    </TwDialog>
  );
});


